import { useEffect, useMemo } from "react";
import { Controller, useWatch } from "react-hook-form";
import {
    Col,
    Form,
} from "react-bootstrap";

// Gestiona la UI del factor K en la pantalla de tarifa.
// Regla principal: Només es mostra en modo indexado.
export const CoeficienteKInput = ({
    control,
    errors,
    modeFacturacio,
    tarifas = [],
    setValue,
    readOnly = false,
}) => {
    const tarifaComercialId = useWatch({ control, name: "tarifa_comercial" });
    const coeficientK = useWatch({ control, name: "coeficient_k" });

    const selectedTarifa = useMemo(
        () => tarifas.find((tarifa) => String(tarifa.id) === String(tarifaComercialId)),
        [tarifas, tarifaComercialId],
    );

    // Si la tarifa comercial contiene CALL, el K queda forzado a 0.
    const isCallTarifa = Boolean(
        selectedTarifa?.name && selectedTarifa.name.toUpperCase().includes("CALL"),
    );

    // Se bloquea por permiso readOnly o por regla de negocio de tarifa CALL.
    const isLocked = Boolean(readOnly || isCallTarifa);
    // En CALL el minimo visual es 0; en resto se mantiene minimo 5.
    const minValue = isCallTarifa ? 0 : 5;

    useEffect(() => {
        // Flag auxiliar para validaciones de formulario/payload.
        setValue("is_call_tarifa", isCallTarifa, { shouldValidate: true, shouldDirty: false });

        // El factor K no aplica fuera de indexadas.
        if (modeFacturacio !== "index") {
            return;
        }

        // Si es CALL, garantizamos persistencia estricta de K=0.
        if (isCallTarifa && Number(coeficientK) !== 0) {
            setValue("coeficient_k", 0, { shouldValidate: true, shouldDirty: true });
            return;
        }

        // En no-CALL normalizamos valores vacios o menores al minimo permitido.
        if (!isCallTarifa && (coeficientK === undefined || coeficientK === null || coeficientK === "" || Number(coeficientK) < 5)) {
            setValue("coeficient_k", 5, { shouldValidate: true, shouldDirty: false });
        }
    }, [coeficientK, isCallTarifa, modeFacturacio, setValue]);

    // En modo fijo/ATR no se renderiza el bloque de K.
    if (modeFacturacio !== "index") return null;

    return (
        <Col xs={12} className="mb-4">
            <div className="bg-light p-3 rounded-4 border border-light">
                <Controller
                    control={control}
                    name="coeficient_k"
                    render={({ field: { onChange, onBlur, value } }) => (
                        <>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Form.Label className="text-muted small fw-bold text-uppercase mb-0">
                                    Coeficiente K (€/MWh)
                                </Form.Label>
                                <div className="d-flex align-items-center gap-2">
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        // Valor mostrado: 0 si CALL; en resto usa valor actual o fallback 5.
                                        value={isCallTarifa ? 0 : (value ?? 5)}
                                        onChange={(e) => {
                                            if (isLocked) return;
                                            // Clamp defensivo para mantener el rango permitido en input manual.
                                            const val = Math.max(5, Math.min(35, parseFloat(e.target.value) || 5));
                                            onChange(val);
                                        }}
                                        min={minValue}
                                        max={35}
                                        disabled={isLocked}
                                        className="text-center fw-bold border-0 bg-white shadow-sm rounded-pill"
                                        style={{ width: '80px' }}
                                    />
                                    <span className="badge bg-primary rounded-pill">€/MWh</span>
                                </div>
                            </div>

                            {!isCallTarifa && (
                                <>
                                    <Form.Range
                                        // Slider sincronizado con el input numerico.
                                        value={isCallTarifa ? 0 : (value ?? 5)}
                                        onChange={(e) => {
                                            if (isLocked) return;
                                            onChange(e);
                                        }}
                                        onBlur={onBlur}
                                        min={minValue}
                                        max={35}
                                        disabled={isLocked}
                                        className="w-100"
                                    />

                                    <div className="d-flex justify-content-between mt-1">
                                        <small className="text-muted">Min: {minValue}</small>
                                        <small className="text-muted">Max: 35</small>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                />
                {errors.coeficient_k?.message && (
                    <div className="text-danger small mt-2 fw-bold">
                        {errors.coeficient_k.message}
                    </div>
                )}
            </div>
        </Col>
    );
};
